<template>
  <div class="card mb-25 border-0 rounded-0 bg-white edit-support-cours-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form
        ref="supportCoursForm"
        @submit="editSupportCours"
        @invalid-submit="handleInvalidSubmit"
        :validation-schema="supportCoursSchema"
        :validateOnInput="false"
        :validateOnMount="false"
        :initialValues="{
          prerequis: prerequis,
          nombreHeures: nombreHeures,
          description: description,
          titre: titre,
          nombreModules: nombreModules,
          estModulaire: estModulaire,
          auteur: auteur,
          fichier: fichier,
          niveau: selectedNiveau,
          filiere: selectedFiliere,
          matiere: selectedMatiere,
          modulesAdded: addedModules
        }"
      >
        <!-- Ligne 1 : Titre + Niveau + Filière (COMME AddSupport) -->
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Titre <span class="text-danger">*</span>
              </label>
              <Field name="titre" v-slot="{ field, errorMessage }">
                <input
                  v-bind="field"
                  type="text"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Titre du support"
                />
                <small v-if="errorMessage" class="text-danger d-block mt-1">
                  {{ errorMessage }}
                </small>
              </Field>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Niveau <span class="text-danger">*</span>
              </label>
              <Field name="niveau" v-slot="{ field, errorMessage, handleChange }">
                <Multiselect
                  :options="globalNiveauxOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  :modelValue="field.value"
                  @update:modelValue="(val) => { handleChange(val); onGlobalNiveauChange(val); }"
                  placeholder="Sélectionner le niveau"
                />
                <small v-if="errorMessage" class="text-danger d-block mt-1">
                  {{ errorMessage }}
                </small>
              </Field>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Filière <span class="text-danger">*</span>
              </label>
              <Field name="filiere" v-slot="{ field, errorMessage, handleChange }">
                <Multiselect
                  :options="globalFilieresOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  :modelValue="field.value"
                  @update:modelValue="(val) => { handleChange(val); onGlobalFiliereChange(val); }"
                  placeholder="Sélectionner la filière"
                  :disabled="!globalSelectedNiveau || isLoadingFilieres"
                />
                <small v-if="errorMessage" class="text-danger d-block mt-1">
                  {{ errorMessage }}
                </small>
              </Field>
            </div>
          </div>
        </div>

        <!-- Ligne 2 : Matière + Nombre d'heures + Auteur (COMME AddSupport) -->
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Matière <span class="text-danger">*</span>
              </label>
              <Field name="matiere" v-slot="{ field, errorMessage, handleChange }">
                <Multiselect
                  :options="globalMatieresOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  :modelValue="field.value"
                  @update:modelValue="handleChange"
                  placeholder="Sélectionner la matière"
                  :disabled="!globalSelectedNiveau || !globalSelectedFiliere || isLoadingMatieres"
                />
                <small v-if="errorMessage" class="text-danger d-block mt-1">
                  {{ errorMessage }}
                </small>
              </Field>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Nombre d'heures <span class="text-danger">*</span>
              </label>
              <Field name="nombreHeures" v-slot="{ field, errorMessage }">
                <input
                  v-bind="field"
                  type="number"
                  min="1"
                  max="1000"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Ex: 60"
                />
                <small v-if="errorMessage" class="text-danger d-block mt-1">
                  {{ errorMessage }}
                </small>
              </Field>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">Auteur</label>
              <Field name="auteur" v-slot="{ field }">
                <input
                  v-bind="field"
                  type="text"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Nom de l'auteur (facultatif)"
                />
              </Field>
            </div>
          </div>
        </div>

        <!-- Ligne 3 : Fichier + Prérequis + Description (COMME AddSupport) -->
        <div class="row">
          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Fichier principal (PDF)
              </label>
              <input
                type="file"
                accept="application/pdf"
                @change="onSupportFichierChange"
                class="form-control shadow-none fs-md-15 text-black"
              />
              <small v-if="supportFichierName" class="text-success d-block mt-1">
                Fichier actuel: {{ supportFichierName }}
              </small>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">Prérequis</label>
              <Field name="prerequis" v-slot="{ field }">
                <textarea
                  v-bind="field"
                  rows="4"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Connaissances requises pour ce cours..."
                ></textarea>
              </Field>
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">Description</label>
              <Field name="description" v-slot="{ field }">
                <textarea
                  v-bind="field"
                  rows="4"
                  class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Description détaillée du support de cours..."
                ></textarea>
              </Field>
            </div>
          </div>
        </div>

        <!-- Checkbox en bas -->
        <div class="row mt-4">
          <div class="col-md-12">
            <div class="form-check">
              <Field name="estModulaire" type="checkbox" v-slot="{ field }">
                <input
                  v-bind="field"
                  type="checkbox"
                  id="estModulaire"
                  class="form-check-input"
                  :checked="field.value"
                  @change="onEstModulaireChange"
                />
              </Field>
              <label class="form-check-label fw-semibold" for="estModulaire">
                Ce support est modulaire (composé de plusieurs modules)
              </label>
            </div>
          </div>
        </div>

        <!-- Section Modules -->
        <div v-if="estModulaire" class="row mt-4">
          <div class="col-md-12">
            <div class="border border-primary mb-10">
              <div class="row d-flex align-items-center justify-content-between fw-bold py-3 bg-primary">
                <div class="col-md-7 ps-4">
                  <h4 class="fs-4 text-white mb-0">Modules du support</h4>
                </div>
                <div class="col-md-5 pe-4">
                  <button
                    class="btn btn-success float-end"
                    type="button"
                    @click="addNewModule"
                    :disabled="!isLastModuleComplete || hasModuleCodeDuplicates"
                  >
                    <i class="fa fa-plus-circle me-1"></i>
                    Ajouter un module
                  </button>
                </div>
              </div>

              <div class="p-4">
                <div v-for="(module, index) in visibleModules" :key="module.tempId || module.id">
                  <div class="row mt-3 align-items-start">
                    <!-- CODE -->
                    <div class="col-md-3">
                      <label class="d-block text-black fw-semibold mb-2">
                        Code <span class="text-danger">*</span>
                      </label>
                      <Field :name="`modulesAdded[${visibleIndex(module)}].code`" v-slot="{ field, errorMessage }">
                        <input
                          v-bind="field"
                          type="text"
                          class="form-control shadow-none text-black"
                          placeholder="Ex: MOD-001"
                          maxlength="50"
                          @input="(e) => onModuleCodeChange(module, e)"
                        />
                        <small v-if="errorMessage" class="text-danger d-block mt-1">
                          {{ errorMessage }}
                        </small>
                      </Field>
                      <div v-if="getModuleCodeDuplicate(module.tempId, module.id)" class="alert alert-danger mt-2 p-2">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        Ce code est déjà utilisé
                      </div>
                    </div>

                    <!-- TITRE -->
                    <div class="col-md-4">
                      <label class="d-block text-black fw-semibold mb-2">
                        Titre <span class="text-danger">*</span>
                      </label>
                      <Field :name="`modulesAdded[${visibleIndex(module)}].titre`" v-slot="{ field, errorMessage }">
                        <input
                          v-bind="field"
                          type="text"
                          class="form-control shadow-none text-black"
                          placeholder="Titre du module"
                          maxlength="200"
                          @input="(e) => onModuleTitreChange(module, e)"
                        />
                        <small v-if="errorMessage" class="text-danger d-block mt-1">
                          {{ errorMessage }}
                        </small>
                      </Field>
                    </div>

                    <!-- FICHIER -->
                    <div class="col-md-4">
                      <label class="d-block text-black fw-semibold mb-2">
                        Fichier du module (PDF) <span class="text-danger" v-if="!module.id">*</span>
                      </label>
                      <input
                        type="file"
                        accept="application/pdf"
                        class="form-control shadow-none text-black"
                        @change="(e) => onModuleFichierUpload(module, e)"
                        :name="`moduleFichier_${index}`"
                      />
                      <Field
                        :name="`modulesAdded[${visibleIndex(module)}].fichierFile`"
                        v-model="module.fichierFile"
                        style="display: none"
                      />
                      <small v-if="module.fichierName" class="text-success d-block mt-1">
                        Fichier: {{ module.fichierName }}
                      </small>
                    </div>

                    <!-- SUPPRIMER -->
                    <div class="col-md-1 d-flex align-items-start pt-4">
                      <button
                        type="button"
                        class="btn btn-danger btn-sm"
                        @click="removeModule(module)"
                        :disabled="visibleModules.length === 1"
                      >
                        <i class="flaticon-delete"></i>
                      </button>
                    </div>
                  </div>

                  <!-- DESCRIPTION -->
                  <div class="row mt-3">
                    <div class="col-md-12">
                      <label class="d-block text-black fw-semibold mb-2">Description du module</label>
                      <Field :name="`modulesAdded[${visibleIndex(module)}].description`" v-slot="{ field }">
                        <textarea
                          v-bind="field"
                          class="form-control shadow-none text-black"
                          rows="3"
                          placeholder="Description du module"
                          @input="(e) => onModuleDescriptionChange(module, e)"
                        ></textarea>
                      </Field>
                    </div>
                  </div>

                  <hr class="my-4" v-if="index < visibleModules.length - 1" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Boutons -->
        <div class="row mt-5">
          <div class="col-md-12">
            <div class="d-flex align-items-center">
              <button
                class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 rounded-1 fs-md-15 fs-lg-16 bg-success"
                type="submit"
                :disabled="isSubmitting || hasModuleCodeDuplicates"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
                {{ isSubmitting ? 'Modification en cours...' : 'Modifier le support de cours' }}
              </button>
              <router-link
                to="/liste-support"
                class="btn btn-danger transition border-0 lh-1 fw-medium"
              >
                <i class="flaticon-delete lh-1 me-1 position-relative top-2"></i>
                Annuler
              </router-link>
            </div>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>


<script lang="ts">
import { defineComponent, onMounted, ref, computed, nextTick } from "vue";
import { Form, Field } from "vee-validate";
import { success, error } from "../../utils/utils";
import { useRouter, useRoute } from "vue-router";
import ApiService from "../../services/ApiService";
import * as Yup from "yup";
import Multiselect from '@vueform/multiselect';

interface Option {
  value: string | number;
  label: string;
}

interface ModuleForm {
  id?: number;
  code: string;
  titre: string;
  description: string;
  fichierFile?: File | null;
  fichierName?: string;
  tempId?: number;
  toDelete?: boolean;
}

export default defineComponent({
  name: "EditSupportCours",
  components: {
    Form,
    Field,
    Multiselect,
  },
  setup() {
    const supportFichierFile = ref<File | null>(null);
    const supportFichierName = ref<string>('');
    const supportCoursForm = ref<any>(null);
    const router = useRouter();
    const route = useRoute();

    // Valeurs du support
    const prerequis = ref("");
    const nombreHeures = ref<number>(0);
    const description = ref("");
    const titre = ref("");
    const nombreModules = ref<number>(1);
    const estModulaire = ref(false);
    const auteur = ref("");
    const fichier = ref("");
    const selectedNiveau = ref<number | null>(null);
    const selectedFiliere = ref<number | null>(null);
    const selectedMatiere = ref<number | null>(null);

    // Options
    const globalNiveauxOptions = ref<Option[]>([]);
    const globalFilieresOptions = ref<Option[]>([]);
    const globalMatieresOptions = ref<any[]>([]);
    const globalSelectedNiveau = ref<number | null>(null);
    const globalSelectedFiliere = ref<number | null>(null);

    // Modules
    const addedModules = ref<ModuleForm[]>([]);
    const isSubmitting = ref(false);
    const isLoadingMatieres = ref(false);
    const isLoadingFilieres = ref(false);
    const moduleCodeDuplicates = ref<Record<number, boolean>>({});

    let tempModuleIdCounter = 1;

    const visibleModules = computed(() => addedModules.value.filter(m => !m.toDelete));
    const visibleIndex = (module: ModuleForm) => addedModules.value.indexOf(module);

    const isLastModuleComplete = computed(() => {
      if (visibleModules.value.length === 0) return true;
      const last = visibleModules.value[visibleModules.value.length - 1];
      // Pour les modules existants, le fichier n'est pas obligatoire
      const fileRequired = !last.id;
      return !!last.code && !!last.titre && (!fileRequired || !!last.fichierFile);
    });

    const hasModuleCodeDuplicates = computed(() => {
      return Object.values(moduleCodeDuplicates.value).some(isDuplicate => isDuplicate);
    });

    // Schema de validation
    const supportCoursSchema = Yup.object().shape({
      titre: Yup.string()
    .required("Le titre est obligatoire")
    .max(200, "Le titre ne peut pas dépasser 200 caractères"),
      nombreHeures: Yup.number()
        .required("Le nombre d'heures est obligatoire")
        .min(1, "Le nombre d'heures doit être au moins 1")
        .max(1000, "Le nombre d'heures ne peut pas dépasser 1000"),
      prerequis: Yup.string().optional(),
      description: Yup.string().optional(),
      auteur: Yup.string().optional(),
      niveau: Yup.number().nullable().required("Le niveau est obligatoire"),
      filiere: Yup.number().nullable().required("La filière est obligatoire"),
      matiere: Yup.number().nullable().required("La matière est obligatoire"),
      estModulaire: Yup.boolean(),
      modulesAdded: Yup.array().when('estModulaire', {
        is: true,
        then: (schema) => schema.of(
          Yup.object().shape({
            code: Yup.string().required("Le code du module est obligatoire").max(50),
            titre: Yup.string().required("Le titre du module est obligatoire").max(200),
            description: Yup.string().optional(),
            // Fichier obligatoire uniquement pour les nouveaux modules (sans id)
            fichierFile: Yup.mixed().test('required-for-new', "Le fichier du module est obligatoire", function(value) {
              const module = this.parent;
              // Si le module a un id (existe déjà) ET un fichierName (a déjà un fichier), pas besoin de nouveau fichier
              if (module.id && module.fichierName) return true;
              // Si c'est un nouveau module (pas d'id), le fichier est obligatoire
              if (!module.id && !value) return false;
              return true;
            }),
          })
        ).min(1, "Un support modulaire doit avoir au moins un module"),
        otherwise: () => Yup.array().notRequired(),
      }),
    }, [['estModulaire', 'estModulaire']]);


    const handleInvalidSubmit = ({ errors }: any) => {
      console.log("Erreurs de validation:", errors);
      error("Veuillez remplir tous les champs obligatoires");
    };

    const checkModuleCodeDuplicates = () => {
      const codesSeen = new Set<string>();
      const duplicates: Record<number, boolean> = {};

      visibleModules.value.forEach((module) => {
        const normalizedCode = module.code.trim().toUpperCase();
        const key = module.tempId || module.id || 0;
        if (normalizedCode && codesSeen.has(normalizedCode)) {
          duplicates[key] = true;
        } else {
          duplicates[key] = false;
          if (normalizedCode) codesSeen.add(normalizedCode);
        }
      });

      moduleCodeDuplicates.value = duplicates;
    };

    const getModuleCodeDuplicate = (tempId?: number, id?: number) => {
      return (tempId && moduleCodeDuplicates.value[tempId]) || (id && moduleCodeDuplicates.value[id]) || false;
    };

    // === CHARGEMENT INITIAL ===
    onMounted(async () => {
      await fetchNiveaux();
      if (route.params.id) {
        await fetchSupportCours(parseInt(route.params.id as string));
      }
    });


const fetchSupportCours = async (id: number) => {
  try {
    const response = await ApiService.get(`/supports/${id}`);
    const support = response.data.data;

    console.log(" Support chargé:", support);
    console.log(" Modules du support:", support.modules);

    prerequis.value = support.prerequis || "";
    titre.value = support.titre || "";
    nombreHeures.value = support.nombreHeures;
    description.value = support.description || "";
    nombreModules.value = support.nombreModules || 1;
    estModulaire.value = support.estModulaire || false;
    auteur.value = support.auteur || "";
    fichier.value = support.fichier || "";
    supportFichierName.value = support.fichier ? support.fichier.split('/').pop() : '';

    selectedNiveau.value = support.filiereNiveauMatiere?.niveau?.id || null;
    selectedFiliere.value = support.filiereNiveauMatiere?.filiere?.id || null;
    selectedMatiere.value = support.filiereNiveauMatiere?.id || null;

    globalSelectedNiveau.value = selectedNiveau.value;
    globalSelectedFiliere.value = selectedFiliere.value;

    await nextTick();

    if (selectedNiveau.value) {
      await fetchFilieresParNiveau(selectedNiveau.value);
    }

    if (selectedNiveau.value && selectedFiliere.value) {
      await fetchMatieresFiltered(selectedNiveau.value, selectedFiliere.value);
    }

    await nextTick();

    await supportCoursForm.value?.setFieldValue('prerequis', prerequis.value);
    await supportCoursForm.value?.setFieldValue('titre', titre.value);
    await supportCoursForm.value?.setFieldValue('nombreHeures', nombreHeures.value);
    await supportCoursForm.value?.setFieldValue('description', description.value);
    await supportCoursForm.value?.setFieldValue('estModulaire', estModulaire.value);
    await supportCoursForm.value?.setFieldValue('auteur', auteur.value);
    await supportCoursForm.value?.setFieldValue('niveau', selectedNiveau.value);
    await supportCoursForm.value?.setFieldValue('filiere', selectedFiliere.value);
    await supportCoursForm.value?.setFieldValue('matiere', selectedMatiere.value);

    console.log(" Valeurs définies - niveau:", selectedNiveau.value, "filiere:", selectedFiliere.value, "matiere:", selectedMatiere.value);

    if (support.modules && support.modules.length > 0) {
      console.log(" Chargement de", support.modules.length, "modules");
      
      addedModules.value = support.modules.map((module: any) => {
        const tempId = tempModuleIdCounter++;
        moduleCodeDuplicates.value[tempId] = false;
        
        console.log("Module chargé:", {
          id: module.id,
          code: module.code,
          titre: module.titre,
          description: module.description,
          fichier: module.fichier
        });
        
        return {
          id: module.id,
          code: module.code || "",           
          titre: module.titre || "",         
          description: module.description || "", 
          fichierFile: null,
          fichierName: module.fichier ? module.fichier.split('/').pop() : '',
          tempId,
          toDelete: false,
        };
      });
      
      await nextTick();
      
      // Définir chaque champ de module individuellement
      for (let i = 0; i < addedModules.value.length; i++) {
        const module = addedModules.value[i];
        console.log(` Initialisation module [${i}]:`, module);
        
        await supportCoursForm.value?.setFieldValue(`modulesAdded[${i}].code`, module.code);
        await supportCoursForm.value?.setFieldValue(`modulesAdded[${i}].titre`, module.titre);
        await supportCoursForm.value?.setFieldValue(`modulesAdded[${i}].description`, module.description);
      }
      
      checkModuleCodeDuplicates();
      console.log("Modules initialisés avec succès");
    } else {
      console.log("Aucun module trouvé pour ce support");
    }

    console.log(" Formulaire initialisé avec succès");
  } catch (err: unknown) {
    console.error(" Erreur lors du chargement:", err);
    error("Erreur lors de la récupération du support de cours");
  }
};

    // === GESTION DES FICHIERS ===
    const onSupportFichierChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        supportFichierFile.value = target.files[0];
        supportFichierName.value = target.files[0].name;
      }
    };

    const onModuleFichierUpload = (module: ModuleForm, event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file && module) {
        module.fichierFile = file;
        module.fichierName = file.name;
        const index = visibleIndex(module);
        supportCoursForm.value?.setFieldValue(`modulesAdded[${index}].fichierFile`, file);
      }
    };

    // === CHARGEMENT DES DONNÉES ===
    const fetchNiveaux = async () => {
      try {
        const response = await ApiService.get('/all/niveaux?limit=0');
        globalNiveauxOptions.value = response.data.data.data.map((n: any) => ({
          value: n.id,
          label: n.libelle,
        }));
      } catch (err) {
        error("Impossible de charger les niveaux");
      }
    };

    const fetchFilieresParNiveau = async (niveauId: number) => {
      isLoadingFilieres.value = true;
      try {
        const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&limit=0`);
        const filiereNiveauMatieres = response.data.data.data;
        const filieresUniques = Array.from(new Set(filiereNiveauMatieres.map((fnm: any) => fnm.filiere.id)))
          .map(id => filiereNiveauMatieres.find((fnm: any) => fnm.filiere.id === id).filiere);
        globalFilieresOptions.value = filieresUniques.map((f: any) => ({
          value: f.id,
          label: f.libelle,
        }));
      } catch (err) {
        globalFilieresOptions.value = [];
        error("Impossible de charger les filières");
      } finally {
        isLoadingFilieres.value = false;
      }
    };

    const fetchMatieresFiltered = async (niveauId: number, filiereId: number) => {
      isLoadingMatieres.value = true;
      try {
        const response = await ApiService.get(`/filiereNiveauMatieres?niveau=${niveauId}&filiere=${filiereId}&limit=0`);
        globalMatieresOptions.value = response.data.data.data.map((fnm: any) => ({
          value: fnm.id,
          label: `${fnm.matiere.libelle}`,
        }));
      } catch (err) {
        globalMatieresOptions.value = [];
        error("Impossible de charger les matières");
      } finally {
        isLoadingMatieres.value = false;
      }
    };

    // === CHANGEMENTS ===
    const onGlobalNiveauChange = async (niveauValue: any) => {
      const niveauId = typeof niveauValue === 'object' ? niveauValue.value : niveauValue;
      globalSelectedNiveau.value = niveauId;
      globalSelectedFiliere.value = null;
      globalFilieresOptions.value = [];
      globalMatieresOptions.value = [];
      
      await supportCoursForm.value?.setFieldValue('niveau', niveauId);
      await supportCoursForm.value?.setFieldValue('filiere', null);
      await supportCoursForm.value?.setFieldValue('matiere', null);
      
      if (globalSelectedNiveau.value) {
        await fetchFilieresParNiveau(globalSelectedNiveau.value);
      }
    };

    const onGlobalFiliereChange = async (filiereValue: any) => {
      const filiereId = typeof filiereValue === 'object' ? filiereValue.value : filiereValue;
      globalSelectedFiliere.value = filiereId;
      globalMatieresOptions.value = [];
      
      await supportCoursForm.value?.setFieldValue('filiere', filiereId);
      await supportCoursForm.value?.setFieldValue('matiere', null);
      
      if (filiereId && globalSelectedNiveau.value) {
        await fetchMatieresFiltered(globalSelectedNiveau.value, filiereId);
      }
    };

    const onEstModulaireChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      estModulaire.value = target.checked;
      supportCoursForm.value?.setFieldValue('estModulaire', target.checked);

      if (estModulaire.value && visibleModules.value.length === 0) {
        addNewModule();
      } else if (!estModulaire.value) {
        addedModules.value.forEach(m => { if (m.id) m.toDelete = true; });
        addedModules.value = addedModules.value.filter(m => m.id);
        moduleCodeDuplicates.value = {};
      }
    };

    const onModuleCodeChange = (module: ModuleForm, event: Event) => {
      const target = event.target as HTMLInputElement;
      module.code = target.value;
      const index = visibleIndex(module);
      supportCoursForm.value?.setFieldValue(`modulesAdded[${index}].code`, target.value);
      checkModuleCodeDuplicates();
    };

    const onModuleTitreChange = (module: ModuleForm, event: Event) => {
      const target = event.target as HTMLInputElement;
      module.titre = target.value;
      const index = visibleIndex(module);
      supportCoursForm.value?.setFieldValue(`modulesAdded[${index}].titre`, target.value);
    };

    const onModuleDescriptionChange = (module: ModuleForm, event: Event) => {
      const target = event.target as HTMLTextAreaElement;
      module.description = target.value;
      const index = visibleIndex(module);
      supportCoursForm.value?.setFieldValue(`modulesAdded[${index}].description`, target.value);
    };

    const addNewModule = () => {
      const newModule: ModuleForm = {
        code: "", titre: "", description: "", fichierFile: null, fichierName: "", tempId: tempModuleIdCounter++, toDelete: false
      };
      addedModules.value.push(newModule);
      moduleCodeDuplicates.value[newModule.tempId!] = false;
    };

    const removeModule = (module: ModuleForm) => {
      if (module.id) module.toDelete = true;
      else {
        const idx = addedModules.value.indexOf(module);
        addedModules.value.splice(idx, 1);
        delete moduleCodeDuplicates.value[module.tempId!];
      }
      checkModuleCodeDuplicates();
    };

    // === SOUMISSION ===
    const editSupportCours = async (values: any, { resetForm }: any) => {
      isSubmitting.value = true;
      const formData = new FormData();

      try {
        console.log("Valeurs du formulaire:", values);

        if (values.estModulaire && visibleModules.value.length === 0) {
          error("Un support modulaire doit avoir au moins un module");
          return;
        }

        // Récupérer l'ID de filiereNiveauMatiere
        let filiereNiveauMatiereId: number;
        if (typeof values.matiere === 'object') filiereNiveauMatiereId = values.matiere.value;
        else filiereNiveauMatiereId = values.matiere;

        formData.append('prerequis', values.prerequis || '');
        formData.append('titre', values.titre || '');
        if (values.nombreHeures) formData.append('nombreHeures', values.nombreHeures.toString());
        formData.append('description', values.description || '');
        formData.append('nombreModules', (values.estModulaire ? visibleModules.value.length : 1).toString());
        formData.append('estModulaire', values.estModulaire.toString());
        formData.append('auteur', values.auteur || '');
        formData.append('filiereNiveauMatiereId', filiereNiveauMatiereId.toString());

        // Ajouter le fichier principal si modifié
        if (supportFichierFile.value) {
          formData.append('fichier', supportFichierFile.value);
        }

        if (values.estModulaire) {
          // Séparer les modules selon leur statut
          const toCreate = visibleModules.value.filter(m => !m.id);
          const toUpdate = visibleModules.value.filter(m => m.id);
          const toDelete = addedModules.value.filter(m => m.toDelete && m.id).map(m => m.id);

          console.log("Modules à créer:", toCreate);
          console.log("Modules à mettre à jour:", toUpdate);
          console.log("Modules à supprimer:", toDelete);

          // Créer un mapping des fichiers basé sur l'index VISIBLE
          let fileIndexCounter = 0;
          const fileIndexMap = new Map<number, number>();

          // Mapper les modules existants d'abord
          toUpdate.forEach((m) => {
            const visibleIdx = visibleModules.value.indexOf(m);
            fileIndexMap.set(visibleIdx, fileIndexCounter++);
          });

          // Puis les nouveaux modules
          toCreate.forEach((m) => {
            const visibleIdx = visibleModules.value.indexOf(m);
            fileIndexMap.set(visibleIdx, fileIndexCounter++);
          });

          console.log("Mapping des index de fichiers:", Array.from(fileIndexMap.entries()));

          // Modules à mettre à jour
          if (toUpdate.length > 0) {
            const modulesUpdateData = toUpdate.map(m => ({
              id: m.id,
              code: m.code.trim(),
              titre: m.titre.trim(),
              description: m.description || null,
              hasFichier: !!m.fichierFile
            }));
            formData.append('modulesToUpdate', JSON.stringify(modulesUpdateData));

            // Ajouter les fichiers modifiés des modules existants
            toUpdate.forEach((m) => {
              if (m.fichierFile) {
                const visibleIdx = visibleModules.value.indexOf(m);
                const fileIdx = fileIndexMap.get(visibleIdx);
                formData.append(`moduleFichier_${fileIdx}`, m.fichierFile);
                console.log(`✓ Ajout fichier module existant ID:${m.id} à l'index ${fileIdx}, nom: ${m.fichierFile.name}`);
              }
            });
          }

          // Modules à créer
          if (toCreate.length > 0) {
            const modulesCreateData = toCreate.map((m) => ({
              code: m.code.trim(),
              titre: m.titre.trim(),
              description: m.description || null,
            }));
            formData.append('modulesToCreate', JSON.stringify(modulesCreateData));

            // Ajouter les fichiers des nouveaux modules
            toCreate.forEach((m) => {
              if (m.fichierFile) {
                const visibleIdx = visibleModules.value.indexOf(m);
                const fileIdx = fileIndexMap.get(visibleIdx);
                formData.append(`moduleFichier_${fileIdx}`, m.fichierFile);
                console.log(`✓ Ajout fichier nouveau module à l'index ${fileIdx}, nom: ${m.fichierFile.name}`);
              }
            });
          }

          // Modules à supprimer
          if (toDelete.length > 0) {
            formData.append('modulesToDelete', JSON.stringify(toDelete));
          }
        }

        // Log du contenu de FormData pour debug
        console.log("Contenu de FormData:");
        for (let pair of formData.entries()) {
          console.log(pair[0], ':', pair[1]);
        }

        const response = await ApiService.put(`/supports/${route.params.id}`, formData);

        if (response.data.code === 200) {
          success("Support modifié avec succès !");
          router.push('/liste-support');
        } else {
          error(response.data.message || "Erreur lors de la modification");
        }
      } catch (err: any) {
        console.error("Erreur lors de la modification:", err);
        error(err.response?.data?.message || "Impossible de modifier le support");
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      supportCoursSchema,
      supportCoursForm,
      globalNiveauxOptions,
      globalFilieresOptions,
      globalMatieresOptions,
      globalSelectedNiveau,
      globalSelectedFiliere,
      addedModules,
      visibleModules,
      visibleIndex,
      estModulaire,
      isSubmitting,
      isLoadingMatieres,
      isLoadingFilieres,
      moduleCodeDuplicates,
      hasModuleCodeDuplicates,
      isLastModuleComplete,
      prerequis, titre, nombreHeures, description, nombreModules, auteur, fichier,
      selectedNiveau, selectedFiliere, selectedMatiere,
      supportFichierName,
      handleInvalidSubmit,
      onGlobalNiveauChange,
      onGlobalFiliereChange,
      onEstModulaireChange,
      onModuleCodeChange,
      onModuleTitreChange,
      onModuleDescriptionChange,
      onSupportFichierChange,
      onModuleFichierUpload,
      addNewModule,
      removeModule,
      editSupportCours,
      getModuleCodeDuplicate,
    };
  },
});
</script>

<style scoped>
@import '@vueform/multiselect/themes/default.css';

.default-btn {
  background: #28a745;
}
.default-btn:hover {
  background: #218838;
}
.default-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.form-check-input:checked {
  background-color: #28a745;
  border-color: #28a745;
}

.alert {
  border-radius: 0.5rem;
}

label.d-block span.text-danger {
  display: inline !important;
  vertical-align: middle;
  margin-left: 4px;
}

.btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

.btn-success:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.text-primary {
  color: #007bff !important;
}

.text-success {
  color: #28a745 !important;
}

.border-primary {
  border-color: #007bff !important;
}

.border-success {
  border-color: #28a745 !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.bg-primary {
  background-color: #007bff !important;
}

.form-check-input.me-2 {
  margin-right: 0.75rem;
}

.badge {
  padding: 0.5em 0.75em;
  font-size: 0.875em;
}

.badge.bg-primary {
  background-color: #007bff !important;
}

.badge.bg-success {
  background-color: #28a745 !important;
}

.mt-30 {
  margin-top: 30px;
}

.row.mt-10 {
  margin-top: 10px;
}

.alert {
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
}

.alert-success {
  background-color: #d4edda;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.marge-droite {
  padding-right: 15px;
  padding-left: 15px;
}

.form-control:disabled,
.multiselect:disabled {
  background-color: #e9ecef;
  opacity: 0.6;
  cursor: not-allowed;
}

.text-danger {
  font-size: 0.875rem;
  margin-top: 0.25rem;
  display: block;
}

.form-group {
  margin-bottom: 1rem;
}

label {
  font-weight: 600;
  margin-bottom: 0.5rem;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  opacity: 1;
}

textarea.form-control {
  resize: vertical;
  min-height: 80px;
}

.default-btn {
  transition: all 0.3s ease;
}

.default-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-danger {
  transition: all 0.3s ease;
}

.btn-danger:hover {
  background-color: #c82333;
  border-color: #bd2130;
}
</style>